import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { MapPin, Search, Bot, Swords, BadgeCheck, XCircle, Loader2 } from "lucide-react";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

const STEP_LABEL = { grid_started: "Finding the business on Google Maps…", grid_rows: "Searching from every point on the map…", search: "Checking search demand…", ai: "Asking Google's AI and ChatGPT…", audit: "Auditing the Google profile…" };

export const GoogleCheck = ({ audit }) => {
  const g = audit.google;
  const step = audit.steps?.google;
  if (step === "error") return <Card className="p-5 text-sm text-amber-700" data-testid="pa-google-error">Google check: {audit.errors?.google}</Card>;
  if (!g) return <Card className="p-5 text-sm text-muted-foreground flex items-center gap-2" data-testid="pa-google-pending"><Loader2 className="h-4 w-4 animate-spin" />Google check starts after the website scan…</Card>;
  const s = g.summary || {};
  const running = step !== "done";
  const ai = g.ai || {};
  return (
    <div className="space-y-5" data-testid="pa-google">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Google Maps grid · &ldquo;{g.keyword}&rdquo;</h2>
          {running && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{STEP_LABEL[audit.google_step] || "Working…"}</span>}
        </div>
        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          <div className="rounded-xl bg-zinc-900 p-3 flex justify-center [&_svg]:max-w-full [&_svg]:h-auto" data-testid="pa-grid-svg" dangerouslySetInnerHTML={{ __html: g.svg || "" }} />
          <div className="space-y-4">
            <p className="text-lg font-semibold" data-testid="pa-grid-headline">{g.headline}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="In top 3" value={`${s.top3Pct ?? 0}%`} testId="pa-stat-top3" />
              <Stat label="Ranked #1" value={`${s.first ?? 0}/${s.points ?? 0}`} testId="pa-stat-first" />
              <Stat label="Avg. rank" value={s.avgRank ?? "—"} testId="pa-stat-avg" />
              <Stat label="Google rating" value={g.target?.rating ? `${g.target.rating}★` : "—"} testId="pa-stat-rating" />
            </div>
            {g.rivals?.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-1">Who&apos;s beating them</div>
                <div className="divide-y rounded-lg border">
                  {g.rivals.slice(0, 5).map((r) => (
                    <div key={r.cid} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="truncate">{r.title} <span className="text-xs text-muted-foreground">{r.rating}★ · {r.reviews} reviews</span></span>
                      <span className="font-semibold text-xs">Top 3 at {r.top3Pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5" data-testid="pa-search">
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Search className="h-4 w-4 text-primary" />What people search</h3>
          <p className="text-sm">{g.search?.headline || (running ? "Checking…" : "Not available.")}</p>
        </Card>
        <Card className="p-5" data-testid="pa-ai">
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Bot className="h-4 w-4 text-primary" />What AI assistants say</h3>
          <p className="text-sm font-medium">{ai.headline || (running ? "Asking…" : "Not available.")}</p>
          {ai.question && <p className="text-xs text-muted-foreground mt-1">Asked once: &ldquo;{ai.question}&rdquo;</p>}
          {(ai.answers || []).map((a) => (
            <div key={a.engine} className="mt-2 text-sm flex items-start gap-2">
              {a.named ? <BadgeCheck className="h-4 w-4 text-emerald-600 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5" />}
              <span><b>{a.engine === "google" ? "Google AI" : "ChatGPT"}:</b> {a.named ? "named them" : "didn't name them"}{a.others?.length ? ` · ${a.named ? "also named" : "named instead"}: ${a.others.slice(0, 4).join(", ")}` : ""}</span>
            </div>
          ))}
        </Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5" data-testid="pa-profile">
          <h3 className="font-semibold mb-2">Google profile audit {g.profile?.checked ? <Badge variant="outline" className="ml-1">{g.profile.passed}/{g.profile.checked} passed</Badge> : null}</h3>
          {(g.profile?.findings || []).map((f) => (
            <div key={f.id} className="flex items-start gap-2 py-1.5 text-sm border-b last:border-0">
              {f.status === "pass" ? <BadgeCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />}
              <div><div>{f.title}</div>{f.status !== "pass" && f.fix && <div className="text-xs text-muted-foreground">{f.fix}</div>}</div>
            </div>
          ))}
          {!g.profile?.findings?.length && <p className="text-sm text-muted-foreground">{running ? "Checking…" : "No Google listing found."}</p>}
        </Card>
        <Card className="p-5" data-testid="pa-rival">
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Swords className="h-4 w-4 text-primary" />Head to head</h3>
          {g.rival ? (
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground truncate">{audit.business_name}</div><div className="text-3xl font-bold">{audit.grader?.overall ?? "—"}</div></div>
              <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground truncate">{g.rival.title}</div><div className="text-3xl font-bold">{g.rival.overall ?? "—"}</div></div>
              <p className="col-span-2 text-xs text-muted-foreground">Website Scores · rival site {g.rival.host}</p>
            </div>
          ) : <p className="text-sm text-muted-foreground">{running ? "Waiting for the map grid…" : "No rival with a website to compare."}</p>}
        </Card>
      </div>
    </div>
  );
};
